import { IUser } from '@order-up/models';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApiService } from '../services/adminApi';
import { getAuthServiceUrl } from '../utils/env';
import { createLogger } from '../utils/logger';
import { clearPersistedQueryCache } from '../utils/queryCacheStorage';
import {
    isDefinitiveNoSession,
    isSessionUnreachable,
    SessionUnreachableError,
} from '../utils/sessionOutcome';

const logger = createLogger('useUserAuth');

/**
 * Defines the query keys used for regular user authentication operations.
 * These are separate from admin operations for security.
 */
export const userAuthKeys = {
    all: ['userAuth'] as const,
    signIn: () => [...userAuthKeys.all, 'signIn'] as const,
    signOut: () => [...userAuthKeys.all, 'signOut'] as const,
    session: () => [...userAuthKeys.all, 'session'] as const,
};

/**
 * A hook to sign in with Firebase ID token and establish user session.
 * This verifies the token with the backend for any authenticated user.
 * Uses adminApiService.signInWithIdToken which works for all users (not just admins).
 *
 * The onSuccess primes `userAuthKeys.session()` from the response payload
 * so AuthGuard's `useUserSessionValidation` reads the user from cache without
 * a second `GET /auth/session` round-trip. Mirrors the existing admin pattern
 * at `useAdminSignIn` (useAdminAuth.ts).
 */
export const useUserSignIn = () => {
    const queryClient = useQueryClient();
    return useMutation<
        { user: IUser; sessionToken: string; expiresAt: string; customToken?: string },
        Error,
        { idToken: string; rememberMe?: boolean }
    >({
        mutationFn: async ({ idToken, rememberMe = false }: { idToken: string; rememberMe?: boolean }) => {
            logger.debug('Starting mutation with adminApiService', { idToken: idToken.substring(0, 20) + '...', rememberMe });
            return adminApiService.signInWithIdToken(idToken, rememberMe);
        },
        onSuccess: (data) => {
            queryClient.setQueryData(userAuthKeys.session(), data.user);
        },
        mutationKey: userAuthKeys.signIn(),
    });
};

/**
 * Module-level guard so rapid repeat clicks on "Log out" don't each fire a
 * fresh teardown. React Query's `mutate()` does not dedupe concurrent calls,
 * and the dropdown closes on click (hiding the disabled state), so without
 * this a user who clicked 2–3 times fired 2–3 parallel sign-outs. The page
 * navigates on completion, so the guard is never reset within a session.
 */
let userSignOutInProgress = false;

/**
 * A hook to sign out and destroy the user session.
 *
 * Sign-out is structured so the redirect is NEVER gated behind a slow network
 * call: `adminApiService.signOut()` does local teardown first (instant) and a
 * time-boxed best-effort server call, and the redirect runs in `finally` no
 * matter what. This fixes the original bug where a hung `/auth/signout` froze
 * the button on "Signing out…" for 1–2 minutes.
 */
export const useUserSignOut = () => {
    const queryClient = useQueryClient();

    return useMutation<
        { message: string },
        Error,
        void
    >({
        mutationFn: async () => {
            if (userSignOutInProgress) {
                logger.debug('Sign-out already in progress — ignoring repeat click');
                return { message: 'Sign-out already in progress' };
            }
            userSignOutInProgress = true;
            logger.debug('Starting comprehensive cross-app logout');

            // Build the redirect target up-front so it is available in `finally`
            // even if teardown throws.
            const authServiceUrl = getAuthServiceUrl() || 'http://localhost:3000';
            const currentUrl = window.location.origin; // Return to home page
            const logoutUrl = `${authServiceUrl}?returnUrl=${encodeURIComponent(currentUrl)}&fromLogout=true`;

            try {
                // Local (instant) + time-boxed best-effort server teardown.
                // adminApiService.signOut never throws.
                await adminApiService.signOut();
                try {
                    queryClient.clear();
                    // In-memory clear is not enough — the persisted slice would
                    // otherwise survive on disk into the next session.
                    clearPersistedQueryCache();
                } catch (error) {
                    logger.warn('Cache clear failed', { error: error instanceof Error ? error.message : String(error) });
                }
            } finally {
                // Redirect ALWAYS happens — and is never blocked by the network.
                logger.debug('Redirecting to auth service from logout', { logoutUrl });
                window.location.href = logoutUrl;
            }

            return { message: 'Logged out successfully' };
        },
        onError: (error) => {
            logger.error('Logout failed', error);
            // Even on error, auth state is cleared so AuthGuard will redirect.
        },
        mutationKey: userAuthKeys.signOut(),
    });
};

/**
 * A hook to validate the current user session.
 * Returns user data if session is valid, null if no session, or throws error if session is invalid.
 * Uses adminApiService.validateSession which works for all authenticated users.
 */
export const useUserSessionValidation = (enabled: boolean = true) => {
    const queryClient = useQueryClient();
    return useQuery<IUser | null, Error>({
        queryKey: userAuthKeys.session(),
        queryFn: async (): Promise<IUser | null> => {
            try {
                const user = await adminApiService.validateSession();
                logger.info('Valid session found', { email: user.email });
                return user;
            } catch (error) {
                // Ghost session: server returned 401 with the typed
                // AUTH_SESSION_INVALID code AND already cleared the session
                // cookie via Set-Cookie. Wipe the React Query cache so the
                // page doesn't briefly serve stale tenant-scoped data
                // before AuthGuard redirects to auth.
                const code = (error as { code?: string } | null)?.code;
                const message = error instanceof Error ? error.message : '';
                if (code === 'AUTH_SESSION_INVALID' || message.includes('AUTH_SESSION_INVALID')) {
                    logger.warn('Ghost session detected — clearing client query cache');
                    queryClient.clear();
                    return null;
                }

                // The server ANSWERED and said "not signed in". `null` is a
                // verdict the guard may act on.
                if (isDefinitiveNoSession(error)) {
                    return null;
                }

                // We never got a verdict (offline, timeout, 5xx, cold API,
                // deployment-protection wall). Returning `null` here used to
                // make a transient network blip indistinguishable from a real
                // sign-out, so AuthGuard bounced a signed-in operator to the
                // auth app. Throw a typed error instead: the query surfaces
                // `error` (not `data: null`) and guards hold their ground.
                if (isSessionUnreachable(error)) {
                    logger.warn('Session probe could not reach the API — holding auth state', {
                        error: message,
                    });
                    throw new SessionUnreachableError(
                        'Could not reach the session endpoint',
                        { cause: error },
                    );
                }

                // Only log genuinely unexpected errors
                logger.error('Unexpected session validation error', error);
                throw error instanceof Error ? error : new Error('Unknown session validation error');
            }
        },
        enabled, // Only run when enabled is true
        retry: (failureCount, error) => {
            // A definitive "not signed in" is not worth retrying.
            if (isDefinitiveNoSession(error)) {
                return false;
            }
            // An unreachable API usually recovers (cold function, blip) — give
            // it a couple of tries before the guard falls back to optimistic
            // rendering off the Firebase session.
            if (error instanceof SessionUnreachableError || isSessionUnreachable(error)) {
                return failureCount < 2;
            }
            return failureCount < 1;
        },
        retryDelay: (attemptIndex) => Math.min(400 * 2 ** attemptIndex, 2000),
        staleTime: 5 * 60 * 1000, // Consider session valid for 5 minutes
        gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    });
};
